/**
 * The page the music is printed on: how large it is, how much of it is margin, and how
 * large the staves standing on it are.
 *
 * Everything here is in **millimetres**, which is what a page actually is. MusicXML states
 * its geometry in *tenths* -- a unit defined as a fortieth of a staff's height, so that a
 * file shrinks and grows with its music -- and that is the right unit inside a file and the
 * wrong one in a document a person edits: in tenths, making the staves larger would silently
 * make the paper larger too. So the document holds millimetres, the exporter divides by the
 * staff height on the way out, and the importer multiplies by it on the way in.
 *
 * What is *not* here is anything an engraver would call fine engraving: no per-element
 * offsets, no line widths, no fonts. The renderer lays the music out from the notes, and
 * carrying a previous engraver's coordinates is what makes converted files an order of
 * magnitude larger than the music in them.
 */

/**
 * How often a bar number is printed: never, at the start of every system, or every N bars.
 *
 * `system` is what a printed score does and what a new score is set to. A number is the
 * other thing engravers ask for -- every bar in a rehearsal score, every fifth or tenth in
 * a long one -- and it is the count of bars between printed numbers, not the number to
 * start at: the numbers themselves are the bars' own, so a pickup stays bar 0.
 */
export type BarNumbers = "none" | "system" | number;

/** The most bars a printed number can be apart, which is as far as an engine will count. */
export const BAR_NUMBER_INTERVAL_MAX = 64;

/** The white border around the music, in millimetres. */
export interface PageMargins {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

/** How a score is laid out on paper. Every length is in millimetres. */
export interface Layout {
  /**
   * The height of a five-line staff, which is how engravers size music.
   *
   * The printed rastral sizes run from about 9.2mm for a beginner's piano book down to
   * about 3.7mm for a pocket score, and those are the bounds the editor holds you to: a
   * staff outside them is not small or large print, it is unreadable or not a page.
   */
  readonly staffHeight: number;
  readonly pageWidth: number;
  readonly pageHeight: number;
  readonly margins: PageMargins;
  /** Between the lowest staff of one system and the highest of the next. */
  readonly systemGap: number;
  /** Between two staves of the same system. */
  readonly staffGap: number;
  /**
   * Print a run of silent bars as one bar with the count written over it.
   *
   * On in a part and off in a score, which is what every engraver does and what
   * {@link extractPart} sets: a player reading one line wants to be told to count
   * thirty bars, and a conductor reading twenty lines needs them all to line up.
   *
   * It is here rather than on a bar because it is a way of printing the bars and not a
   * thing written in them -- the runs themselves are worked out from the part whenever
   * they are needed, by `document/multirest.ts`, so an edit cannot leave a stale count
   * behind.
   */
  readonly multiRests: boolean;
  /**
   * Leave a staff out of a system where its part has nothing to play.
   *
   * Off by default, and off in a new score, because of what it costs an editor: a staff
   * that is not drawn cannot be clicked, and the rests it would have drawn are exactly
   * where a user clicks to start writing the part that is silent. It is offered because a
   * conductor's score of twenty parts is unreadable without it -- the page is mostly empty
   * staves -- and because that is the page a printed score is judged by.
   *
   * It is a way of printing the bars rather than a thing written in them, as the multi-bar
   * rests above are: the bars are all still there, and turning it off draws them again.
   */
  readonly hideEmptyStaves: boolean;
  /**
   * How often the bars are numbered.
   *
   * Another way of printing the bars rather than something written in them: the numbers
   * are not stored anywhere, they are each bar's place in the piece counted the way a
   * musician counts it, so an inserted bar renumbers everything after it by itself.
   */
  readonly barNumbers: BarNumbers;
}

/** The smallest and largest staff a page is allowed, in millimetres. */
export const STAFF_HEIGHT_RANGE = { min: 3.7, max: 9.2 } as const;

/** A page a score can be set on, by the name a printer would ask for it by. */
export interface PageSize {
  readonly name: string;
  readonly width: number;
  readonly height: number;
}

/** The papers offered, portrait, smallest first. */
export const PAGE_SIZES: readonly PageSize[] = [
  { name: "A5", width: 148, height: 210 },
  { name: "Letter", width: 215.9, height: 279.4 },
  { name: "A4", width: 210, height: 297 },
  { name: "Legal", width: 215.9, height: 355.6 },
  { name: "Tabloid", width: 279.4, height: 431.8 },
  { name: "A3", width: 297, height: 420 },
];

/** A4, a 7mm staff and a 15mm border: what a new score is set on. */
export const DEFAULT_LAYOUT: Layout = {
  staffHeight: 7,
  pageWidth: 210,
  pageHeight: 297,
  margins: { left: 15, right: 15, top: 15, bottom: 15 },
  systemGap: 12,
  staffGap: 8,
  multiRests: false,
  hideEmptyStaves: false,
  barNumbers: "system",
};

/** Tenths in one staff height, by MusicXML's own definition of the unit. */
export const TENTHS_PER_STAFF = 40;

/** Millimetres as the tenths a file states them in. */
export function tenthsOf(millimetres: number, staffHeight: number): number {
  return (millimetres * TENTHS_PER_STAFF) / staffHeight;
}

/** Tenths from a file as the millimetres they measure. */
export function millimetresOf(tenths: number, staffHeight: number): number {
  return (tenths * staffHeight) / TENTHS_PER_STAFF;
}
